/* eslint-disable @next/next/no-img-element */
//components
import {
  Kicker,
  KickerDot,
  SectionTitle,
  SectionLede,
  Grad,
} from '@/containers-ai/SectionHeading';
import { PillLink } from '@/containers-ai/Buttons';
import { CountUp } from '@/containers-ai/CountUp';

import {
  PlatformScale,
  platformScaleStats,
} from '@/containers-ai/PlatformScale';

//styles
import {
  LiveSection,
  BigProof,
  BigProofValue,
  BigProofText,
  ExceptionsBanner,
  ExceptionsBannerText,
  TaskGrid,
  TaskCard,
  TaskTitle,
  TaskDescription,
  TaskMetricWrap,
  TaskMetricText,
  TaskMetric,
  TaskMetricLabel,
  TaskCustomerBox,
  TaskCustomerLogo,
  CustomCard,
  CustomCardKicker,
  CustomCardTitle,
  CustomCardCopy,
  PlusDivider,
  SummaryRow,
  SummaryTitle,
  SummaryPanel,
  SummaryValue,
  SummaryCopy,
} from './LiveInProduction.styles';
import { Container } from '@/components/Grid';

const tasks = [
  {
    slug: 'document-collection-and-filing',
    title: 'Document collection & filing',
    description:
      'Quotes, tenders, appointments, settlement — executed live inside your operation, under the rules you set.',
    metric: '70–90%',
    label: 'less document-handling time',
    customer: null,
  },
  {
    slug: 'tracking-and-status-updates',
    title: 'Tracking & status updates',
    description:
      'We get status from carriers so your team stops making check calls, and push clean updates to the people who need them.',
    metric: '5–10',
    label: 'minutes saved per tracked load',
    customer: null,
  },
  {
    slug: 'tender-failures',
    title: 'Tender failures',
    description:
      'When a tender falls through, we re-tender down your routing guide and re-book, before the miss ever reaches your team.',
    metric: '8–10%',
    label: 'overpayment on flat rates eliminated',
    customer: null,
  },
  {
    slug: 'proactive-load-audits',
    title: 'Proactive load audits',
    description:
      'We check every load before pickup and flag at-risk transit early, so problems surface before they turn into penalties.',
    metric: '30–40%',
    label: 'reduction in avoidable penalty charges',
    customer: null,
  },
  {
    slug: 'claims-processing',
    title: 'Claims processing',
    description:
      'We gather the documentation, validate the claim, and draft the response for your approval, so claims get filed and recovered.',
    metric: '2x',
    label: 'faster claims recovery with higher filing rates',
    customer: null,
  },
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & re-scheduling',
    description:
      'We book dock appointments against carrier and facility availability, and when a pickup slips, we re-schedule and notify everyone who needs to know.',
    metric: '5–10',
    label: 'minutes saved per appointment',
    customer: null,
  },
];

export function LiveInProduction() {
  return (
    <LiveSection id="ai">
      <Container>
        <Kicker>
          <KickerDot />
          What we automate
        </Kicker>
        <SectionTitle style={{ maxWidth: '905px' }}>
          The repetitive work that eats your team&rsquo;s day. <br />
          <Grad>We take it off your plate.</Grad>
        </SectionTitle>
        <SectionLede>
          Every shipper runs the same manual tasks over and over. Loadsmart AI
          automates the tasks, end-to-end.
        </SectionLede>

        <BigProof>
          <BigProofValue>
            <CountUp value={3000} />+
          </BigProofValue>
          <BigProofText>
            real TMS objects <b>orchestrated in production by Loadsmart AI</b>
          </BigProofText>
        </BigProof>

        <ExceptionsBanner>
          <ExceptionsBannerText>
            Loadsmart Freight Experts resolve the exceptions
          </ExceptionsBannerText>
        </ExceptionsBanner>

        <TaskGrid>
          {tasks.map((task) => (
            <TaskCard key={task.title} href={`/ai/${task.slug}/`}>
              <TaskTitle>{task.title}</TaskTitle>
              <TaskDescription>{task.description}</TaskDescription>
              <TaskMetricWrap>
                <TaskMetricText>
                  <TaskMetric>{task.metric}</TaskMetric>
                  <TaskMetricLabel>{task.label}</TaskMetricLabel>
                </TaskMetricText>
                {task.customer && (
                  <TaskCustomerBox>
                    <TaskCustomerLogo
                      src={task.customer.img}
                      alt={task.customer.alt}
                    />
                  </TaskCustomerBox>
                )}
              </TaskMetricWrap>
            </TaskCard>
          ))}
        </TaskGrid>

        <CustomCard>
          <div>
            <CustomCardKicker>Custom &mdash; anything else</CustomCardKicker>
            <CustomCardTitle>
              If you can describe the task, we&rsquo;ll build the agent to run
              it.
            </CustomCardTitle>
            <CustomCardCopy>
              Bring the repetitive, rules-based work that eats your team&rsquo;s
              day. We build it, we run it, and our Freight Experts own the
              exceptions.
            </CustomCardCopy>
          </div>
          <PillLink href="/contact/#contact-form" className="qlf-btn-2">
            Book a working session &#8594;
          </PillLink>
        </CustomCard>

        <PlusDivider aria-hidden="true">+</PlusDivider>

        <SummaryRow>
          <SummaryTitle>
            The more Loadsmart AI runs your freight,{' '}
            <span>the more it resolves on its own.</span>
          </SummaryTitle>
          <SummaryPanel>
            <SummaryValue>80% to 95%+</SummaryValue>
            <SummaryCopy>
              Loadsmart AI senses, decides and acts on every load, then writes
              the outcome back into your system of record.{' '}
              <b>
                It compounds on your own outcomes so it resolves more on its own
                over time.
              </b>
            </SummaryCopy>
          </SummaryPanel>
        </SummaryRow>
        <PlatformScale stats={platformScaleStats} />
      </Container>
    </LiveSection>
  );
}
